import axios from 'axios';
import { auth } from '../../../firebase';
import { cleanJsonEmptyValues } from '../../../utils/formatUtils';
import { Access } from '../../Interfaces/admin/Role.interfaces';
import { CompanySource } from '../../Interfaces/admin/User.interfaces';
import { CompanyFormFields } from '../../features/admin/companies/CompanyForm';

const apiUrl = process.env['NX_API_URL'];

export const fetchUserCompanies = async () => {
  const response = await axios.get(`${apiUrl}/api/company/user`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const data = response.data;
  return data;
};

export const fetchCompany = async (id: string, signal?: AbortSignal) => {
  const response = await axios.get(`${apiUrl}/api/company/${id}`, {
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const data = response.data;
  return data;
};

export const fetchCompanies = async (
  params?: {
    source?: CompanySource;
    info?: string;
    page?: number;
    amountPerPage?: number;
    isActive?: boolean;
  },
  signal?: AbortSignal
) => {
  const cleanedParams = cleanJsonEmptyValues(params ?? {});

  const response = await axios.get(`${apiUrl}/api/company`, {
    signal,
    params: cleanedParams,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const data = response.data;
  return data;
};

export const fetchSourceCompanies = async (
  source: CompanySource,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/company/source`, {
    signal,
    params: { source },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const data = response.data;
  return data;
};

export const postCompany = async (data: CompanyFormFields) => {
  const cleanedData = cleanJsonEmptyValues(data);

  const response = await axios.post(`${apiUrl}/api/company`, cleanedData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const putCompany = async (id: string, data: CompanyFormFields) => {
  const cleanedData = cleanJsonEmptyValues(data);

  const response = await axios.put(`${apiUrl}/api/company/${id}`, cleanedData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const putCompanyStatus = async (id: string, isActive: boolean) => {
  const response = await axios.put(
    `${apiUrl}/api/company/${id}/status`,
    { isActive },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  return response.data;
};

export type getUsersArgs = {
  page: number;
  amountPerPage: number;
  userType?: string;
  isAdmin?: boolean;
  roles?: string[];
  info?: string;
  isActive?: boolean;
  companies?: string[];
};

export const getUsers = async (params: getUsersArgs, signal?: AbortSignal) => {
  const cleanedParams = cleanJsonEmptyValues(params);

  const response = await axios.get(`${apiUrl}/api/user`, {
    signal,
    params: cleanedParams,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const getUser = async (userID: string, signal?: AbortSignal) => {
  const response = await axios.get(`${apiUrl}/api/user/${userID}`, {
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const getUserSettings = async (signal?: AbortSignal) => {
  const response = await axios.get(`${apiUrl}/api/user/settings`, {
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response.data;
};

export const patchEmailNotificationsEnabled = async (
  emailNotificationsEnabled: boolean
) => {
  const response = await axios.patch(
    `${apiUrl}/api/user/emailNotifications`,
    { emailNotificationsEnabled },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export type usersArgs = {
  firstname: string;
  lastname: string;
  company?: string[];
  roles: string[];
  isAdmin: boolean;
  userType: string;
  access?: Access[];
};

export type postUsersArgs = usersArgs & {
  email: string;
};

export const postUsers = async (data: postUsersArgs) => {
  const cleanedData = cleanJsonEmptyValues(data);

  const response = await axios.post(`${apiUrl}/api/user`, cleanedData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const putUserAuth = async () => {
  const response = await axios.put(
    `${apiUrl}/api/user/auth`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const putUsers = async (userID: string, data: usersArgs) => {
  const cleanedData = cleanJsonEmptyValues(data);

  const response = await axios.put(
    `${apiUrl}/api/user/${userID}`,
    cleanedData,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const postResetPassword = async (email: string) => {
  const response = await axios.post(
    `${apiUrl}/api/user/resetPassword`,
    { email },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
  const result = response.data;
  return result;
};

export const postDeleteUsers = async (users: string[]) => {
  const response = await axios.post(
    `${apiUrl}/api/user/delete`,
    { users },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const postEnableUsers = async (users: string[]) => {
  const response = await axios.post(
    `${apiUrl}/api/user/enable`,
    { users },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const deleteUser = async (userID: string) => {
  const response = await axios.delete(`${apiUrl}/api/user/${userID}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const result = response.data;
  return result;
};

export const enableUser = async (userID: string) => {
  const response = await axios.post(
    `${apiUrl}/api/user/enable/${userID}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );
  const result = response.data;
  return result;
};

export const fetchViewPointUsers = async () => {
  const response = await axios.get(`${apiUrl}/api/user/vp`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  const data = response.data;
  return data;
};

export const fetchViewPointJobsSecurityGroups = async () => {
  const response = await axios.get(`${apiUrl}/api/user/vp/jobsSecurityGroups`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  const data = response.data;
  return data;
};
